import React, { useState, useEffect, useMemo, useRef } from "react";
import { 
  X, Play, Pause, RotateCcw, Film, School, MapPin, 
  BarChart3, Clock3, Bell, Volume2, VolumeX, Eye, 
  FileDown, Share2, FileEdit, CheckCircle2, Maximize2,
  ChevronRight, Activity, Zap, ShieldCheck
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, ReferenceLine, AreaChart, Area
} from "recharts";
import ReplayMapLite from "./ReplayMapLite";

// Import des données réelles
import travelHistoryData from "src/pages/trafficPage/data/TravelHistory.json";

// Transformation des données réelles avec calcul de durée dynamique
const transformRealData = (raw) => {
  const points = raw?.data?.getTravelHistory?.points || [];
  
  // TRIER les points par timestamp (ordre chronologique)
  const sortedPoints = [...points].sort((a, b) => parseInt(a.ts) - parseInt(b.ts));
  
  const firstTs = parseInt(sortedPoints[0]?.ts || 0);
  const lastTs = parseInt(sortedPoints[sortedPoints.length - 1]?.ts || 0);
  const durationInMinutes = Math.max(1, Math.ceil((lastTs - firstTs) / 60));

  return {
    tripId: "TRIP-DLA-2026",
    vehicleId: "BUS-YELLOW-01",
    driverName: "Jean Dupont", // Nom du conducteur (à récupérer depuis l'API)
    mode: "Ramassage",
    date: "01 Mars 2026",
    duration: durationInMinutes,
    distance: raw?.data?.getTravelHistory?.distanceTravelledKms || 0,
    maxSpeed: Math.max(...sortedPoints.map(p => p.speed), 0),
    violationsCount: sortedPoints.filter(p => p.speed > 30).length,
    speedData: sortedPoints.map(p => ({
      time: new Date(parseInt(p.ts) * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      speed: p.speed
    })),
    positions: sortedPoints.map(p => ({
      lat: p.lat,
      lng: p.lng,
      speed: p.speed,
      isViolation: p.speed > 30
    })),
    events: [
      { time: new Date(firstTs * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), type: "start", label: "Début du trajet", icon: "🟢" },
      { time: new Date(lastTs * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), type: "end", label: "Fin du trajet", icon: "🟓" },
    ]
  };
};

const PRESENTATION_DATA = transformRealData(travelHistoryData);

/**
 * TripReplayModal - Version Indigo Originale avec Itinéraire Réel
 */
const TripReplayModal = ({ isOpen, onClose, tripData, loading }) => {
  const finalData = useMemo(() => tripData || PRESENTATION_DATA, [tripData]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [dashcamEnabled, setDashcamEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [reviewed, setReviewed] = useState(false);

  useEffect(() => {
    let interval;
    if (isPlaying && finalData) {
      interval = setInterval(() => {
        setCurrentTime((prev) => {
          const increment = 1 * playbackSpeed;
          if (prev >= finalData.duration * 60) {
            setIsPlaying(false);
            return finalData.duration * 60;
          }
          return prev + increment;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, playbackSpeed, finalData]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4 animate-in fade-in duration-300">
      <div className="w-full max-w-[95vw] h-[90vh] flex flex-col rounded-[2.5rem] border border-white/20 bg-white/95 shadow-[0_30px_90px_rgba(0,0,0,0.4)] overflow-hidden">
        
        {/* --- HEADER INDIGO --- */}
        <header className="px-8 py-5 border-b border-slate-200/60 bg-white flex items-center justify-between z-10">
          <div className="flex items-center gap-5">
            <div className="h-14 w-14 rounded-2xl bg-indigo-50 flex items-center justify-center border border-indigo-100 shadow-sm">
              <Film className="w-7 h-7 text-indigo-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-black text-slate-900 tracking-tight">
                  Replay Trajet : <span className="text-indigo-600 uppercase">{finalData.tripId}</span>
                </h3>
                <span className="px-2.5 py-0.5 rounded-full bg-indigo-600 text-[10px] font-black text-white uppercase tracking-widest">
                  {finalData.mode}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-1">
                <span className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                  <School className="w-3.5 h-3.5 text-indigo-400" /> {finalData.vehicleId}
                </span>
                {finalData.driverName && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-slate-300" />
                    <span className="flex items-center gap-1.5 text-xs font-bold text-slate-600">
                      <span className="text-indigo-500">👤</span> {finalData.driverName}
                    </span>
                  </>
                )}
                <span className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-xs font-bold text-slate-400">{finalData.date}</span>
              </div>
            </div>
          </div>
          
          <button onClick={onClose} className="p-3 rounded-2xl border border-slate-200 hover:bg-red-50 hover:border-red-100 hover:text-red-500 transition-all active:scale-95 group">
            <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" />
          </button>
        </header>

        {/* --- MAIN CONTENT --- */}
        <div className="flex-1 overflow-y-auto bg-slate-50/50 p-6 custom-scrollbar">
          <div className="space-y-6">
            
            {/* KPIs Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { label: "Durée", value: `${finalData.duration} min`, icon: Clock3, color: "blue" },
                { label: "Distance", value: `${finalData.distance} km`, icon: MapPin, color: "emerald" },
                { label: "Vitesse Max", value: `${finalData.maxSpeed} km/h`, icon: Zap, color: "amber", highlight: true },
                { label: "Violations", value: finalData.violationsCount, icon: Bell, color: "red", highlight: finalData.violationsCount > 0 }
              ].map((kpi, idx) => (
                <div key={idx} className="bg-white p-5 rounded-3xl border border-slate-200/60 shadow-sm flex items-center justify-between group hover:shadow-md transition-shadow">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{kpi.label}</p>
                    <p className={`text-2xl font-black ${kpi.highlight ? `text-${kpi.color}-600` : 'text-slate-900'}`}>{kpi.value}</p>
                  </div>
                  <div className={`p-3 rounded-2xl bg-${kpi.color}-50 text-${kpi.color}-600 group-hover:scale-110 transition-transform`}>
                    <kpi.icon className="w-6 h-6" />
                  </div>
                </div>
              ))}
            </div>

            {/* Visualization Section */}
            <div className="grid grid-cols-12 gap-5">
              <div className="col-span-12 lg:col-span-8 rounded-[2rem] border border-slate-200 bg-white overflow-hidden shadow-sm h-[550px] relative">
                <ReplayMapLite
                  positions={finalData.positions}
                  currentTime={currentTime}
                  duration={finalData.duration}
                  showViolations={true}
                />
              </div>

              <div className="col-span-12 lg:col-span-4 flex flex-col gap-5 h-[550px]">
                <div className="flex-1 bg-white rounded-[2rem] border border-slate-200 p-6 shadow-sm flex flex-col min-h-0">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                      <Activity className="w-4 h-4 text-indigo-600" /> Profil Vitesse
                    </h4>
                  </div>
                  <div className="flex-1 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={finalData.speedData}>
                        <defs>
                          <linearGradient id="speedGradientMod" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="time" hide />
                        <YAxis hide domain={[0, 80]} />
                        <Tooltip 
                          contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '11px', fontWeight: 'bold' }}
                          formatter={(v) => [`${v} km/h`, 'Vitesse']}
                        />
                        <ReferenceLine y={30} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={1.5} />
                        <Area type="monotone" dataKey="speed" stroke="#6366f1" strokeWidth={3} fill="url(#speedGradientMod)" isAnimationActive={false} />
                        <ReferenceLine x={finalData.speedData[Math.floor((currentTime / (finalData.duration * 60)) * (finalData.speedData.length - 1))]?.time} stroke="#1e293b" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="h-[250px] bg-white rounded-[2rem] border border-slate-200 p-6 shadow-sm flex flex-col">
                  <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Clock3 className="w-4 h-4 text-indigo-600" /> Chronologie
                  </h4>
                  <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-3">
                    {finalData.events.map((event, idx) => (
                      <div key={idx} className={`flex items-start gap-3 p-3 rounded-2xl border transition-all ${event.type === 'violation' ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100 opacity-60'}`}>
                        <span className="text-[10px] font-black text-slate-400 min-w-[55px]">{event.time}</span>
                        <span className="text-sm">{event.icon}</span>
                        <span className={`text-[11px] font-bold ${event.type === 'violation' ? 'text-red-700' : 'text-slate-700'}`}>{event.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- FOOTER CONTROLS --- */}
        <footer className="bg-white border-t border-slate-200/60 p-8 z-10">
          <div className="max-w-7xl mx-auto flex flex-col gap-6">
            <div className="flex items-center gap-6">
              <span className="text-[11px] font-black text-slate-400 w-12 tabular-nums">{formatTime(currentTime)}</span>
              <div className="flex-1 relative group h-6 flex items-center">
                <input
                  type="range"
                  min={0}
                  max={finalData.duration * 60}
                  value={currentTime}
                  onChange={(e) => setCurrentTime(Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer accent-indigo-600 group-hover:h-2 transition-all"
                />
              </div>
              <span className="text-[11px] font-black text-slate-400 w-12 tabular-nums">{formatTime(finalData.duration * 60)}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-slate-50 p-1.5 rounded-2xl border border-slate-200/50 shadow-inner">
                  <button onClick={() => setIsPlaying(!isPlaying)} className={`h-12 w-12 rounded-xl flex items-center justify-center transition-all active:scale-90 ${isPlaying ? 'bg-white text-indigo-600 shadow-sm' : 'bg-indigo-600 text-white shadow-indigo-200'}`}>
                    {isPlaying ? <Pause className="w-6 h-6 fill-current" /> : <Play className="w-6 h-6 fill-current translate-x-0.5" />}
                  </button>
                  <button onClick={() => setCurrentTime(0)} className="h-10 w-10 flex items-center justify-center text-slate-400 hover:text-indigo-600 transition-colors">
                    <RotateCcw className="w-5 h-5" />
                  </button>
                  <select value={playbackSpeed} onChange={(e) => setPlaybackSpeed(Number(e.target.value))} className="bg-transparent border-none text-[11px] font-black text-slate-600 px-3 cursor-pointer outline-none">
                    <option value={0.5}>0.5x</option><option value={1}>1.0x</option><option value={2}>2.0x</option><option value={4}>4.0x</option><option value={8}>8.0x</option><option value={10}>10.0x</option>
                  </select>
                </div>

                <div className="flex gap-2">
                  <button onClick={() => setDashcamEnabled(!dashcamEnabled)} className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest border transition-all ${dashcamEnabled ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-slate-200 text-slate-400'}`}>
                    <Eye className="w-4 h-4" /> Dashcam {dashcamEnabled ? 'ON' : 'OFF'}
                  </button>
                  <button onClick={() => setSoundEnabled(!soundEnabled)} className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest border transition-all ${soundEnabled ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-slate-200 text-slate-400'}`}>
                    {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} Audio
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button className="p-3 bg-white border border-slate-200 rounded-2xl text-slate-600 hover:bg-slate-50 transition-all shadow-sm"><FileEdit className="w-5 h-5" /></button>
                <button className="p-3 bg-white border border-slate-200 rounded-2xl text-slate-600 hover:bg-slate-50 transition-all shadow-sm"><Share2 className="w-5 h-5" /></button>
                <button className="p-3 bg-white border border-slate-200 rounded-2xl text-slate-600 hover:bg-slate-50 transition-all shadow-sm"><FileDown className="w-5 h-5" /></button>
                <button onClick={() => setReviewed(!reviewed)} className={`flex items-center gap-3 px-8 py-3 rounded-2xl text-sm font-black transition-all shadow-lg active:scale-95 ${reviewed ? 'bg-emerald-600 text-white shadow-emerald-200' : 'bg-indigo-600 text-white shadow-indigo-200'}`}>
                  {reviewed ? <ShieldCheck className="w-5 h-5" /> : <CheckCircle2 className="w-4 h-4" />}
                  {reviewed ? "Dossier Revu" : "Marquer comme Revu"}
                </button>
              </div>
            </div>
          </div>
        </footer>

        <style dangerouslySetInnerHTML={{ __html: `
          .custom-scrollbar::-webkit-scrollbar { width: 5px; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        `}} />
      </div>
    </div>
  );
};

export default TripReplayModal;
